import { AGENT_TYPES, UploadDialogState } from "@/modules/agent/types";
import UploadDialog from "@/modules/agent/components/upload-dialog";
import { useGetTeamsListQuery } from "@/modules/auth/requests";
import { getTPRAVendorsListColumns } from "@/modules/agent/components/TPRADashboard/constants/columns";
import { useServerSideState } from "@/modules/agent/components/TPRADashboard/hooks/useServerSideState";
import { useGetTPRAVendors } from "@/modules/agent/components/TPRADashboard/requests/getTPRAVendors";
import type { TPRAVendorDetails } from "@/modules/agent/components/TPRADashboard/types/vendors";
import { ZButton } from "@/shared/components/button";
import ZBreadCrumbs from "@/shared/components/custom/breadcrumbs";
import { LoadingSpinner } from "@/shared/components/custom/spin";
import ErrorBoundary from "@/shared/components/error-boundary";
import { ZTableDeprecrated } from "@/shared/components/table-deprecrated/TableDeprecrated";
import { ZTablePagination } from "@/shared/components/TablePagination";
import { SearchField } from "@/shared/components/TableSearch";
import { ROUTES } from "@/shared/constants/routes";
import { Select, SelectItem, cn, type SortDescriptor } from "@nextui-org/react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function TPRAVendorsPage() {
  const navigate = useNavigate();

  // Modal state
  const [uploadDialogState, setUploadDialogState] = useState<UploadDialogState>({
    isOpen: false,
  });

  // Server-side state management
  const { queryParams, pagination, search, sort, filters } = useServerSideState({
    page: 1,
    page_size: 25,
  });

  // Fetch team data for member name mapping
  const { data: teamsData } = useGetTeamsListQuery({ isEnabled: true });
  const memberIdNameMapping = useMemo(
    () => teamsData?.memberIdNameMapping,
    [teamsData?.memberIdNameMapping]
  );

  // Fetch vendors from API with member mapping
  const { data, isLoading, isFetching, isError, error } =
    useGetTPRAVendors(queryParams, memberIdNameMapping);

  // Extract data with defaults
  const vendors = data?.vendors ?? [];
  const totalVendors = data?.total_vendors ?? 0;
  const totalPages = data?.total_pages ?? 0;
  const currentPage = data?.page ?? 1;

  // Handle opening the modal
  const handleNewAssessment = () => {
    setUploadDialogState({
      isOpen: true,
      type: AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
    });
  };

  // Handle severity click - navigate to Issues page with params
  const handleSeverityClick = (vendorName: string, severity: string) => {
    navigate(`/third-party-risk/issues?vendor=${encodeURIComponent(vendorName)}&severity=${encodeURIComponent(severity)}`);
  };

  // Memoize columns with severity click callback
  const columns = useMemo(
    () => getTPRAVendorsListColumns(handleSeverityClick),
    [handleSeverityClick]
  );

  // Handle sort changes from table
  const handleSortModelChange = (sortDescriptor: SortDescriptor) => {
    const column = String(sortDescriptor.column);

    if (!column) {
      sort.resetSort();
      return;
    }

    // Map column names to API sort fields
    const columnToFieldMap: Record<string, string> = {
      vendor_product_name: "vendor_name",
      risk_rating: "risk_rating",
      last_assessed_at: "last_assessed_at",
      total_issues: "total_issues",
    };

    const sortBy = columnToFieldMap[column] as
      | "vendor_name"
      | "risk_rating"
      | "last_assessed_at"
      | "total_issues"
      | undefined;

    if (sortBy) {
      sort.setSort(sortBy);
    }
  };

  return (
    <ErrorBoundary fallback={null}>
      <div className="w-full h-full flex flex-col">
        {/* Top Bar: Breadcrumb + New Assessment Button */}
        <div className="flex items-center pb-4 justify-between min-h-[44px] max-h-[44px]">
          <div className="flex gap-4 items-center">
            <ZBreadCrumbs
              items={[
                { title: "Third-Party Risk", href: `/${ROUTES.THIRD_PARTY_HOME}` },
                { title: "All Third-Parties", href: `/${ROUTES.THIRD_PARTY_VENDORS}` },
              ]}
            />
          </div>
          <ZButton
            color="primary"
            className="text-xs font-medium h-7 px-3"
            onClick={handleNewAssessment}
          >
            New Assessment
          </ZButton>
        </div>

        {/* Main Content */}
        <div className="h-full w-full overflow-hidden rounded-lg flex flex-col">
          {/* Header: Title + Count | Status Filter + Search */}
          <div className="px-6 py-4 flex flex-col bg-white gap-2">
            <div className="flex flex-row justify-between items-center gap-2 flex-shrink-0">
              {/* Left: Title + Count */}
              <div className="flex flex-row items-center gap-2 flex-grow">
                <h2 className="font-['Inter'] font-medium text-[14px] leading-[20px] text-[#11181C]">
                  All Third-Parties
                </h2>
                {!isLoading && !isFetching && (
                  <div className="flex flex-row justify-center items-center py-[1px] px-2 gap-1 bg-[#F4F4F5] rounded-[45px]">
                    <span className="font-['Inter'] font-medium text-[10px] leading-[16px] text-[#52525B]">
                      {totalVendors}
                    </span>
                  </div>
                )}
              </div>

              {/* Right: Status Filter + Search */}
              <div className="flex flex-row items-center gap-2">
                <Select
                  placeholder="All Statuses"
                  selectedKeys={[filters.status ?? "all"]}
                  onChange={(e) => {
                    const value = e.target.value;
                    filters.setStatus(
                      value === "all"
                        ? undefined
                        : (value as
                            | "intake_form"
                            | "due_diligence"
                            | "review"
                            | "reassessment"
                            | "complete")
                    );
                  }}
                  className="w-[160px]"
                  classNames={{
                    trigger: "h-[36px] bg-white border-1 border-[#E4E4E7] rounded-[6px]",
                    value: "font-['Inter'] font-normal text-[14px] leading-[20px] text-[#3F3F46]",
                  }}
                >
                  <SelectItem key="all" value="all">
                    All Statuses
                  </SelectItem>
                  <SelectItem key="intake_form" value="intake_form">
                    New Assessment
                  </SelectItem>
                  <SelectItem key="due_diligence" value="due_diligence">
                    Due Diligence
                  </SelectItem>
                  <SelectItem key="review" value="review">
                    In Review
                  </SelectItem>
                  <SelectItem key="reassessment" value="reassessment">
                    Reassessment
                  </SelectItem>
                  <SelectItem key="complete" value="complete">
                    Reviewed
                  </SelectItem>
                </Select>

                <SearchField
                  value={search.searchTerm}
                  onChange={search.setSearchTerm}
                  placeholder="Search by name"
                  className="w-[420px]"
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="relative flex-1 min-h-0">
              {/* Loading overlay - only during refetch, not initial load */}
              {!isLoading && isFetching && (
                <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center rounded-lg">
                  <LoadingSpinner />
                </div>
              )}
              <ZTableDeprecrated<TPRAVendorDetails>
                className={cn(
                  "border-1 border-[#E4E4E7] overflow-auto scroll-auto h-full"
                )}
                layout="fixed"
                loading={isLoading || isFetching}
                rows={vendors}
                columns={columns}
                isHeaderSticky
                isCompact
                removeWrapper
                getRowId={(row) => row.uid ?? row.id}
                tableHeaderProps={{ className: "shadow-none rounded-none" }}
                tableRowProps={(row, rowIndex) => ({
                  className: cn(rowIndex === 0 ? "border-none" : "border-y", "group"),
                })}
                classNames={{
                  wrapper: "!shadow-none !rounded-none",
                  base: "!shadow-none !rounded-none",
                  th: "!shadow-none !rounded-none !bg-gradient-to-b !from-[#f5f2f4] !to-[rgb(245,242,244)]",
                }}
                aria-label="TPRA Vendors List"
                onSortModelChange={handleSortModelChange}
                tableBodyProps={{
                  emptyContent: isLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <LoadingSpinner
                        containerClassName="bg-transparent relative"
                        className="mx-auto"
                      />
                    </div>
                  ) : isError ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <p className="text-red-500 text-sm font-medium">
                        Failed to load vendors
                      </p>
                      <p className="text-gray-500 text-xs mt-1">
                        {error?.message ?? "An unexpected error occurred"}
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <p className="text-gray-500 text-sm">No vendors found</p>
                    </div>
                  ),
                }}
              />
            </div>
            <ZTablePagination
              totalPages={totalPages}
              paginationState={{
                currentPage: currentPage,
                rowsPerPage: pagination.pageSize,
              }}
              setPagination={{
                handlePageChange: (page) => pagination.setPage(page),
                handleRowsPerPageChange: (rows) => pagination.setPageSize(rows),
              }}
              className="px-5 !py-3 border-t border-[#E4E4E7]"
            />
          </div>
        </div>
      </div>

      {/* Upload Dialog */}
      <UploadDialog data={uploadDialogState} setIsOpen={setUploadDialogState} />
    </ErrorBoundary>
  );
}
